/**
 * Screen-space Level-of-Detail (LOD) bucket policy (PR-J).
 *
 * # Why screen-space LOD
 *
 * On the 685k-entity П35Р fixture, zooming all the way out turns 95%
 * of the geometry into sub-pixel noise. Tessellating arcs / splines
 * for entities that occupy < 1 px on screen wastes the GPU's time and
 * the CPU's vertex-buffer churn budget. The LOD policy classifies
 * every visible entity into one of four buckets driven solely by its
 * world-bbox diagonal × camera zoom (= pixel size on screen):
 *
 *   * `skip`      — < 0.5 px : entity is dropped from the frame entirely.
 *   * `point`     — < 2  px  : entity rendered as a single point in a
 *                              shared `THREE.Points` cloud (one giant
 *                              PointsMaterial, no per-entity cost).
 *   * `bbox-line` — < 8  px  : entity rendered as the four straight
 *                              line segments of its axis-aligned bbox
 *                              — skips arc / spline / hatch
 *                              tessellation entirely.
 *   * `full`      — ≥ 8  px  : full geometry, identical to the PR-B
 *                              path.
 *
 * # Pixels = worldDiagonal × cameraZoom
 *
 * In the OrthographicCamera (Q4 2D mode), `1 world unit × cameraZoom
 * = 1 CSS pixel`. The world diagonal of an entity is
 * `hypot(maxX-minX, maxY-minY)`; multiplying by `cameraZoom` yields
 * its on-screen extent. We treat the diagonal (not max(width,height))
 * because it bounds the *visible silhouette* of any rotated geometry
 * — a 100-unit-wide line rotated 45° still occupies √2 × 100 px.
 *
 * # Hysteresis (see lodTransitions.ts)
 *
 * Plain thresholds at 0.5 / 2 / 8 px would pop-flicker on slow zoom
 * because frame-to-frame floating-point jitter pushes entities
 * across the boundary. `lodTransitions.ts` adds a 10% buffer around
 * each boundary (e.g. 1.8 - 2.2 px is "previous bucket"); the
 * `lodBucketForEntity` helper here is the *boundary-only* classifier
 * — callers wrap it with the hysteresis layer when continuity matters.
 *
 * # Why not view frustum cull instead
 *
 * Frustum culling drops entities outside the camera; LOD reduces the
 * cost of those *inside* it. They are complementary; the rbush
 * spatial index already handles the "outside frustum" case via
 * `intersect(viewportRect)`. PR-J's LOD only kicks in once an entity
 * has survived viewport culling.
 */

import type { RenderableEntity } from "../types";
import {
  shouldSkipTextRun,
  type TextRunScreenBucket,
} from "../text/textScreenGate";
import {
  bboxAreaWorld,
  shouldUseSolidFallback,
} from "../hatch/hatchScreenGate";

/**
 * The four LOD buckets the renderer recognises. New buckets must be
 * added here AND handled by every consumer of `lodBucketForEntity`
 * — there is no default-fall-through behaviour.
 */
export type LodBucket = "skip" | "point" | "bbox-line" | "full";

/**
 * Pixel thresholds that delimit bucket boundaries. Exposed as a
 * `const` so callers (and the hysteresis layer) can introspect them
 * without re-deriving the numbers.
 */
export const LOD_THRESHOLDS_PX = {
  /** Below this — drop the entity entirely. */
  skip: 0.5,
  /** Below this — render as a single point. */
  point: 2,
  /** Below this — render as the bbox outline. */
  bboxLine: 8,
} as const;

/**
 * Codex PR #784 round-9 P2 (`state/spatialBuilder.ts:213`) — world half-size the
 * renderer's own DEGENERATE-geometry fallback draws, in world units.
 *
 * `adapters/sceneGeometryBuilders.ts buildPointSceneGeometry` gives a POINT
 * whose PDSIZE is 0 a bbox of `position ± 1` — "a small fixed bbox of 1 world
 * unit so the spatial index can still hit-test the point" — because AutoCAD
 * still PAINTS a PDMODE marker there (screen-relative, 5% of viewport height at
 * PDSIZE 0; see `adapters/pointMarkerMeshes.ts markerWorldRadius`). Mirrored
 * here (that module is the renderer's, this one is the LOD policy's) so the two
 * agree on the extent of something the renderer draws but whose ANALYTIC extent
 * is zero.
 */
export const LOD_DEGENERATE_HALF_WORLD = 1;

/**
 * Screen-space diagonal (in world units) attributed to an entity whose bbox is
 * exactly DEGENERATE — zero width AND zero height.
 *
 * # Why a degenerate bbox may not be 0 px
 *
 * A zero-diagonal entity multiplied by ANY zoom stays 0 px, so the classifier
 * would return `"skip"` at every zoom and the entity could never be recovered:
 * not by `partitionStubsByLod` at bulk load, not by the per-frame relift, and —
 * before this — not by the metadata-stub resolver's sub-pixel floor
 * (`state/spatialBuilder.ts createMetadataStubResolver`), which multiplies the
 * same zero by the live zoom. That is a PERMANENT drop, the exact failure mode
 * removing kind-level exemptions from {@link isAlwaysLiftedStubType} is only
 * safe without.
 *
 * Zero-diagonal bboxes are not hypothetical: a DXF POINT bounds to its single
 * location (`drawing-model viewportMembership.ts pointBounds`), so an INSERT
 * whose definition holds only a POINT — and equally an INSERT of an EMPTY,
 * MISSING or CYCLIC definition, which falls back to the bare insertion point —
 * carries a zero-size metadata extent.
 *
 * # Why the marker box is the honest number
 *
 * The renderer does NOT draw nothing for those: once lifted, a POINT's
 * `RenderableEntity.bounds` IS the PDMODE/PDSIZE marker box
 * ({@link LOD_DEGENERATE_HALF_WORLD}), which is non-degenerate. So this floor
 * does not invent an extent — it makes the STUB's LOD extent agree with the
 * extent the SAME entity gets the moment it is lifted, instead of letting the
 * two disagree forever. An INSERT of an empty / missing definition still lifts
 * to `null` (nothing to draw) and costs one resolve, never a mesh.
 *
 * Entities with a genuinely small-but-nonzero extent are untouched: they cross
 * the 0.5 px boundary on their own as the camera zooms in, which is the whole
 * point of the LOD policy.
 *
 * # It is a RECOVERABILITY measure, NOT a claim that pixels are painted
 *
 * Codex PR #784 round-10 P2 (`lod/lodPolicy.ts:166`) — the synthetic diagonal
 * says only "this id must stay reachable by the resolver and the lift"; it does
 * NOT say the renderer draws anything there. `liftBlockInsertRenderable`
 * returns `null` for an EMPTY / MISSING definition, so treating its stub like a
 * 2x2-world-unit object all the way down the pipeline put it in `groups.point`
 * whenever the synthetic diagonal landed in the 0.5-2 px band, and
 * `buildPointsCloud` then painted a one-pixel dot for a reference with no
 * geometry at all. {@link entityDrawsDegenerateMarker} is where the two are
 * separated: only an entity whose OWN renderer output at a zero analytic extent
 * is a point marker (a genuine DXF POINT — `adapters/pointMarkerMeshes.ts`) may
 * be represented by the point-cloud proxy; every other degenerate entity is
 * measured for the LIFT decision only ({@link lodBucketForEntity}).
 */
export const LOD_DEGENERATE_WORLD_DIAGONAL = Math.hypot(
  2 * LOD_DEGENERATE_HALF_WORLD,
  2 * LOD_DEGENERATE_HALF_WORLD,
);

/**
 * Compute the on-screen pixel size of a world bbox at the given scale.
 * Returns the bbox *diagonal* in pixels — the conservative bound for any
 * rotated silhouette.
 *
 * `scalePx` is CSS pixels per world unit. For the MAIN camera that is
 * `camera.zoom`; for model geometry seen through a paper-space VIEWPORT it is
 * the viewport's OWN model scale (`viewports/viewportLiftRequests.ts
 * viewportModelScalePx`) — the same bbox is sub-pixel in one and large in the
 * other, so every LOD decision must name which scale it is made at.
 *
 * A DEGENERATE (zero-diagonal) bbox is measured as the renderer's marker box
 * instead of as 0 — see {@link LOD_DEGENERATE_WORLD_DIAGONAL}. That is a
 * RECOVERABILITY measure only: whether such an entity may be DRAWN as a
 * point-cloud dot is decided by {@link entityDrawsDegenerateMarker}, not here.
 *
 * Public so the hysteresis layer, the metadata-stub resolver's sub-pixel floor
 * and unit tests all measure with the same function.
 */
export function boundsScreenSizePx(
  bounds: {
    readonly minX: number;
    readonly minY: number;
    readonly maxX: number;
    readonly maxY: number;
  },
  scalePx: number,
): number {
  const w = Math.max(0, bounds.maxX - bounds.minX);
  const h = Math.max(0, bounds.maxY - bounds.minY);
  const raw = Math.hypot(w, h);
  const diagonal = raw > 0 ? raw : LOD_DEGENERATE_WORLD_DIAGONAL;
  // Negative / zero scale is non-physical; clamp to a positive value
  // so the classifier degenerates to `skip` instead of NaN.
  const z = scalePx > 0 ? scalePx : 0;
  return diagonal * z;
}

/**
 * Compute the on-screen pixel size of an entity from its world bbox
 * and the camera zoom. Thin wrapper over {@link boundsScreenSizePx} — the
 * entity-shaped call site the hysteresis layer and the bucket classifier use.
 */
export function entityScreenSizePx(
  entity: RenderableEntity,
  cameraZoom: number,
): number {
  return boundsScreenSizePx(entity.bounds, cameraZoom);
}

/**
 * Is this entity a text-like primitive (TEXT / MTEXT)?
 *
 * Entity type strings are `"Text"` / `"MText"` (see
 * `adapters/index.ts` `entityToRenderable` switch + `EntityPreview["type"]`).
 *
 * Text entities have a SMALL world bbox, so at a fit-to-extents
 * `cameraZoom` on a large drawing they always bucket as `"skip"` by the
 * bbox-diagonal rule (`lodBucketForEntity`). They MUST be exempt from
 * the cache-direct LOD pre-classify *drop* (`partitionStubsByLod` in
 * `scene/sceneBulkLoadHelpers.ts`).
 *
 * Why the generic per-frame re-lift does NOT cover them: the
 * viewport-cull re-lift IS wired (`FrameLoops` diffs
 * `groups.full ∪ groups.bboxLine` and calls `batchedLayer.addEntity`),
 * but it only feeds BatchedLayer *line* geometry — `flattenForBucket`
 * returns `[]` for `"text"` / `"mtext"` (batchedLayer.ts), so re-adding a
 * text id builds no glyphs. Text meshes are built ONLY by `emitTextMeshes`
 * at bulk-load, which the re-lift never re-invokes. So a text stub dropped
 * here never reaches `emitTextMeshes` for the WHOLE session — a permanent
 * drop = invisible labels on large drawings (the reported bug).
 *
 * Always-lifting text is AutoCAD-parity: world-space glyph meshes are
 * naturally sub-pixel at extents and the LOD lift-drop no longer hides
 * them. (A separate, pre-existing PR-V5 per-glyph height gate
 * `shouldSkipTextRun` still applies at emit-time and is NOT re-emitted on
 * zoom-in; on the streamed fit-extents path `v5Zoom = 1` so real text
 * clears it.) The realistic text count is hundreds to low-thousands, so
 * the always-lift cost is negligible; tens-of-thousands-MText files are
 * bounded by the async chunked lift path and remain the text-only re-lift
 * follow-up.
 */
export function isTextLikeEntityType(type: string): boolean {
  return type === "Text" || type === "MText";
}

/**
 * S9b (Codex P2 — "Recreate MLEADER decorators on LOD relift") — entity types
 * that carry BULK-LOAD-ONLY direct decorator / text meshes the per-frame
 * viewport-cull re-lift cannot rebuild. A DIMENSION's arrow / tick heads +
 * measurement text, and a MULTILEADER's leader arrows + synthesised MTEXT /
 * block content, are emitted ONLY by `emitFillAndArrayMeshes` / `emitTextMeshes`
 * at bulk-load; the re-lift path (`FrameLoops`) feeds ONLY BatchedLayer *line*
 * geometry via `addEntity`. So if such a stub is dropped as sub-pixel at
 * fit-to-extents, zooming in would restore its leader/dim LINES but never its
 * arrows / text — a permanent partial render until the next full bulk-load.
 *
 * Exempting them from the sub-pixel LOD drop (exactly as `isTextLikeEntityType`
 * exempts TEXT / MTEXT, and for the same "re-lift can't rebuild it" reason)
 * guarantees the decorator + text meshes always exist. Realistic dimension /
 * multileader counts (hundreds–low-thousands) make the always-lift cost
 * negligible, and it is AutoCAD-parity (annotations are never silently dropped).
 */
export function isDecoratorEntityType(type: string): boolean {
  return type === "Dimension" || type === "Multileader";
}

/**
 * Is this entity type ALWAYS lifted at cache-direct bulk-load, regardless of
 * its on-screen size at the fit-to-extents zoom? The single predicate
 * `partitionStubsByLod` (`scene/sceneBulkLoadHelpers.ts`) consults for the
 * sub-pixel `"skip"` pre-classify DROP — the union of:
 *
 *   - {@link isTextLikeEntityType} — TEXT / MTEXT;
 *   - {@link isDecoratorEntityType} — DIMENSION / MULTILEADER.
 *
 * # `Block` (INSERT) is NOT here — Codex PR #784 round-8 P2
 *
 * It used to be, because a stub dropped at bulk-load could never be re-lifted:
 * the post-bulk-load spatial resolver was `renderableById.get(id)` over the
 * LIFTED renderables only, and `computeVisibleSet` (`lod/visibleSet.ts`) DROPS
 * every rbush hit that resolves to `undefined`, so a dropped stub never reached
 * the on-demand `entityResolverRef` that could rebuild it. Exempting `Block`
 * was the only way to make INSERTs render on the cache-direct path — at the
 * price the finding names: on a drawing with many repeated references, the
 * initial pass lifted EVERY one of them and each lift recursively materialised
 * its whole `childRenderables` tree (`scene/liftBlockInsert.ts`). Measured on a
 * 20 000-INSERT / 50-children-per-definition synthetic where every reference is
 * sub-pixel at fit-to-extents: 20 000 wrappers + 1 000 000 children in ~2.2 s of
 * pure lift, before a single pixel is drawn
 * (`scene/__tests__/cacheDirectInsertLodScale.test.ts`).
 *
 * The recovery path the old doc called a follow-up now exists, so the drop is
 * no longer permanent and the exemption is gone:
 *
 *   1. RESOLVER FALLBACK — the cache-direct pass chains
 *      `createMetadataStubResolver` (`state/spatialBuilder.ts`) behind
 *      `renderableById`, so an id that was never lifted resolves to its
 *      METADATA STUB (real bbox, built on demand, nothing retained) and keeps
 *      flowing through the visible set into Pass 2's newly-visible diff.
 *   2. PASS-2 RELIFT COMPLETENESS — `entityResolverRef`
 *      (`SceneBulkLoad.ts finishSceneBulkLoad`) publishes the lifted parent AND
 *      its satellites back into `renderableById` (so pick / window-select /
 *      OSNAP see real geometry) and calls `emitReliftDirectMeshes`
 *      (`scene/sceneBulkLoadHelpers.ts`), which builds the fill / arrow / glyph
 *      / array meshes `BatchedLayer.addEntity` cannot — through the SAME two
 *      emitters the bulk load uses.
 *
 * Net: a sub-pixel INSERT costs one metadata stub at startup and materialises
 * its children the first frame it is big enough to be drawn.
 *
 * The per-frame viewport cull is unchanged: a lifted Block wrapper whose extent
 * goes sub-pixel is culled like any other lifted entity (its satellites go with
 * it, `FrameLoops` Pass 1) and re-lifted on zoom-in (Pass 2 re-expands them from
 * the wrapper).
 *
 * # Why the remaining exemptions stay (for now)
 *
 * TEXT / MTEXT / DIMENSION / MULTILEADER are mechanically recoverable through
 * the same two steps, but removing them is a separate change with its own
 * verification surface: DIMENSION / MULTILEADER are ALSO pinned to `"full"` in
 * the per-frame hysteresis classifier (`lodTransitions.ts
 * lodBucketWithHysteresis`), so dropping the initial-lift exemption without
 * revisiting that pin would leave two rules disagreeing about the same kinds.
 * They are cheap (hundreds–low-thousands per drawing) and carry no child tree,
 * so they are not the scalability problem this predicate was flagged for.
 *
 * Deliberately SEPARATE from `isDecoratorEntityType`: that predicate ALSO pins
 * its kinds to `"full"` per-frame, which for INSERTs would have exempted every
 * block reference from viewport LOD culling for the whole session.
 */
export function isAlwaysLiftedStubType(type: string): boolean {
  return isTextLikeEntityType(type) || isDecoratorEntityType(type);
}

/**
 * True when `bounds` has NO analytic extent at all — zero width AND zero
 * height. Exactly the case {@link LOD_DEGENERATE_WORLD_DIAGONAL} substitutes a
 * synthetic diagonal for, exposed so the bucket rule can tell "measured" from
 * "assumed".
 */
export function isDegenerateBounds(bounds: {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}): boolean {
  return bounds.maxX - bounds.minX <= 0 && bounds.maxY - bounds.minY <= 0;
}

/**
 * Codex PR #784 round-10 P2 — does the renderer PAINT something for this entity
 * at a DEGENERATE (zero) analytic extent?
 *
 * A genuine DXF POINT does: `adapters/sceneGeometryBuilders.ts
 * buildPointSceneGeometry` gives it the PDMODE/PDSIZE marker box and
 * `adapters/pointMarkerMeshes.ts` draws the marker, so collapsing it to one dot
 * in the shared points cloud is an honest proxy for what it looks like at 1 px.
 *
 * Nothing else does. Every other zero-extent record on the cache-direct path is
 * a METADATA STUB (`state/metadataStub.ts isMetadataStubRenderable`) standing in
 * for an entity the renderer never lifted — most sharply an INSERT of an EMPTY,
 * MISSING or POINT-only definition, all of which carry the bare insertion point
 * as their extent while only the POINT-only one lifts to anything drawable
 * (`scene/liftBlockInsert.ts liftBlockInsertRenderable` returns `null` for the
 * other two). Their synthetic marker extent exists so the resolver and the lift
 * can still reach them ({@link LOD_DEGENERATE_WORLD_DIAGONAL}); it is not
 * evidence of pixels, so they must never be handed to the points cloud — see
 * {@link lodBucketForBounds}.
 *
 * A POINT's own STUB is a `Point` too (`state/spatialBuilder.ts
 * metadataToSpatialStub` keeps the real `type`), so the proxy survives the
 * cache-direct path exactly where it is earned.
 */
export function entityDrawsDegenerateMarker(entity: RenderableEntity): boolean {
  return entity.type === "Point";
}

/**
 * The ONE bucket rule for a measured pixel size plus the extent it was measured
 * from — `lodBucketForPixels` with the degenerate-extent restriction applied.
 *
 * `drawsDegenerateMarker` is {@link entityDrawsDegenerateMarker} for the entity
 * the bounds belong to (the bounds-only call sites — the paper-space viewport
 * request pass — pass `false`, the conservative value: a stub they cannot
 * resolve is never treated as drawable).
 *
 * The restriction: an entity with NO analytic extent whose renderer paints no
 * marker cannot be a `"point"` proxy, because the dot would assert geometry that
 * only the lift can confirm. It is `"skip"` until its synthetic marker box
 * reaches the LIFT band ({@link LOD_THRESHOLDS_PX.point}), where the lift itself
 * settles the question — a POINT-only INSERT materialises its marker-drawing
 * child, an empty / missing one resolves to `null` and stays non-rendering, and
 * a CYCLIC one never gets here at all (the recursion placeholder gives it a real
 * footprint, `cad-core blockDefinitionWalk.ts`).
 */
export function lodBucketForBounds(args: {
  pixels: number;
  degenerate: boolean;
  drawsDegenerateMarker: boolean;
  /** Pre-computed bucket, when the caller already applied hysteresis. */
  bucket?: LodBucket;
}): LodBucket {
  const bucket = args.bucket ?? lodBucketForPixels(args.pixels);
  if (bucket !== "point") return bucket;
  if (!args.degenerate || args.drawsDegenerateMarker) return bucket;
  return "skip";
}

/**
 * Buckets whose entities the renderer MATERIALISES: `scene/FrameLoops.tsx`
 * lifts exactly `groups.full ∪ groups.bboxLine` into `BatchedLayer` and
 * deliberately leaves `groups.point` (drawn by the shared points cloud, no
 * per-entity geometry) and `groups.skip` (not drawn at all) out of it.
 *
 * Codex PR #784 round-10 P1 (`viewports/viewportLiftRequests.ts:181`) — exported
 * so the paper-space VIEWPORT request pass asks the SAME question at ITS scale
 * instead of restating a second, looser rule ("anything not `skip`"). That
 * second rule requested every sub-pixel reference a viewport happened to show:
 * 20 000 one-pixel INSERTs of a 50-child definition merged into
 * `nextVisibleSet` resolve to 20 000 wrappers and ~1 000 000 children — the
 * exact blow-up round 8 removed, re-created through the viewport path.
 */
export function lodBucketIsLifted(bucket: LodBucket): boolean {
  return bucket === "full" || bucket === "bbox-line";
}

/**
 * Boundary-only classifier — returns the LOD bucket an entity falls
 * into at the given camera zoom, without any continuity guarantees.
 *
 * Boundary semantics (matches plan PR-J):
 *   * `pixels < 0.5`  → `"skip"`
 *   * `pixels < 2`    → `"point"`
 *   * `pixels < 8`    → `"bbox-line"`
 *   * `pixels >= 8`   → `"full"`
 */
export function lodBucketForEntity(
  entity: RenderableEntity,
  cameraZoom: number,
): LodBucket {
  const px = entityScreenSizePx(entity, cameraZoom);
  // Codex PR #784 round-10 P2 — a zero-extent entity that paints no marker is
  // never a points-cloud proxy; see `lodBucketForBounds`.
  return lodBucketForBounds({
    pixels: px,
    degenerate: isDegenerateBounds(entity.bounds),
    drawsDegenerateMarker: entityDrawsDegenerateMarker(entity),
  });
}

/**
 * Classify a pre-computed pixel size into a bucket. Useful when the
 * caller already has the size in hand (e.g. the hysteresis layer that
 * wants to short-circuit on the previous frame's value).
 */
export function lodBucketForPixels(pixels: number): LodBucket {
  if (pixels < LOD_THRESHOLDS_PX.skip) return "skip";
  if (pixels < LOD_THRESHOLDS_PX.point) return "point";
  if (pixels < LOD_THRESHOLDS_PX.bboxLine) return "bbox-line";
  return "full";
}

/**
 * PR-V5 — secondary bucket classifier for text entities driven by the
 * entity's *primary* run height (not the bbox diagonal). This is a
 * specialisation of the bbox-diagonal classifier above: a 100-unit
 * MText with sub-pixel height *content* still has a wide bbox (the
 * runs paint horizontally), so the bbox classifier fails to skip it.
 * The text gate looks at the run height directly so emission cost is
 * gated on what the user can actually perceive (glyph height in CSS
 * pixels).
 *
 * Returns a `TextRunScreenBucket`:
 *   * `"skip"`   — drop the entity entirely (sub-pixel)
 *   * `"dot"`    — render a single Points dot at the entity centre
 *   * `"render"` — full glyph quads / strokes
 *
 * Returns `"render"` for entities whose geometry kind is not text /
 * mtext — non-text callers should use `lodBucketForEntity` instead.
 */
export function textEntityScreenBucket(
  entity: RenderableEntity,
  cameraZoom: number,
): TextRunScreenBucket {
  const g = entity.geometry;
  if (g.kind !== "text" && g.kind !== "mtext") return "render";
  return shouldSkipTextRun(g.height, cameraZoom);
}

/**
 * PR-V5 — secondary bucket classifier for hatch entities. Returns
 * `true` when the hatch should fall back to a solid-coloured mesh
 * (pattern texture imperceptible at this zoom). The first-class LOD
 * bucket (skip/point/bbox-line/full) still drives the *whether* of
 * emission — this helper drives only the *pattern vs. solid* choice
 * for hatches that survived the LOD cull.
 *
 * Returns `false` for entities whose geometry kind is not hatch.
 */
export function hatchEntityUsesSolidFallback(
  entity: RenderableEntity,
  cameraZoom: number,
): boolean {
  const g = entity.geometry;
  if (g.kind !== "hatch") return false;
  // SOLID hatches are already flat-colour; the gate is irrelevant.
  if (g.solid) return false;
  return shouldUseSolidFallback(bboxAreaWorld(entity.bounds), cameraZoom);
}
